import React from 'react';
import { Popover, Transition } from '@headlessui/react';
import { Calendar } from 'react-date-range';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { Colors } from '@core/config';
import { useThemeStore } from '@core/theme/useThemeStore';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface SingleDatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  isInvalid?: boolean;
  // Where the calendar panel should anchor relative to the trigger button.
  panelAlignment?: 'left' | 'right';
  displayFormat?: string;
  // Extra classes merged into the trigger button.
  className?: string;
}

const SingleDatePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  isInvalid,
  panelAlignment = 'right',
  displayFormat = 'D MMM YYYY',
  className,
}: SingleDatePickerProps) => {
  const theme = useThemeStore();
  const accentColor = theme?.theme?.primaryBrandColor || Colors.LINK_BLUE;

  return (
    <Popover className="relative w-full min-w-0">
      {({ open, close }) => (
        <>
          <Popover.Button
            aria-invalid={isInvalid || undefined}
            className={`flex items-center justify-between gap-2 rounded-sm border bg-white px-3 py-2 text-sm font-medium ${
              isInvalid
                ? 'border-red-500 ring-1 ring-red-500'
                : open
                  ? 'ring-2 ring-purple-500'
                  : ''
            } ${className ?? ''}`}
          >
            <span>{dayjs(value).format(displayFormat)}</span>
            <FontAwesomeIcon
              icon={faCalendar}
              className="shrink-0 text-gray-500"
              aria-hidden
            />
          </Popover.Button>
          <Transition
            as={React.Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              portal={false}
              anchor={{
                to: panelAlignment === 'right' ? 'bottom end' : 'bottom start',
                gap: '0.5rem',
              }}
              transition
              className="z-[100] rounded-lg border border-gray-200 bg-white shadow-lg outline-hidden"
            >
              <Calendar
                date={value}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(date: Date) => {
                  onChange(dayjs(date).startOf('day').toDate());
                  close();
                }}
                color={accentColor}
              />
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default SingleDatePicker;
